import { useCallback, useRef } from 'react'
import { Track, LocalParticipant, RemoteParticipant } from 'livekit-client'

export type ParticipantInfo = {
  participant: LocalParticipant | RemoteParticipant
  videoTrack?: Track
  audioTrack?: Track
}

export interface ParticipantVideoProps {
  participantInfo: ParticipantInfo
  variant: 'pinned' | 'small'
  showControls?: boolean
  onClick?: () => void
  isLocalParticipant?: boolean
}

export function ParticipantVideo({
  participantInfo,
  variant,
  showControls = true,
  onClick,
  isLocalParticipant = false
}: ParticipantVideoProps) {
  const { participant, videoTrack, audioTrack } = participantInfo
  const identity = participant.identity
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const hasVideo = !!videoTrack && !videoTrack.isMuted
  const hasAudio = !!audioTrack && !audioTrack.isMuted
  const isSpeaking = participant.isSpeaking
  const isPinned = variant === 'pinned'

  const setVideoRef = useCallback((element: HTMLVideoElement | null) => {
    if (videoRef.current && videoTrack) {
      videoTrack.detach(videoRef.current)
    }
    videoRef.current = element
    if (element && videoTrack) {
      videoTrack.attach(element)
    }
  }, [videoTrack])

  const setAudioRef = useCallback((element: HTMLAudioElement | null) => {
    if (audioRef.current && audioTrack) {
      audioTrack.detach(audioRef.current)
    }
    audioRef.current = element
    if (element && audioTrack && !isLocalParticipant) {
      audioTrack.attach(element)
    }
  }, [audioTrack, isLocalParticipant])

  const handleClick = () => {
    if (!isPinned && onClick) onClick()
  }

  return (
    <div
      className={`relative bg-neutral-900 rounded-md overflow-hidden border-2 border-transparent transition-all ${
        isPinned ? 'w-full h-full min-h-[400px]' : 'w-[120px] h-[90px] min-h-[90px] hover:scale-[1.05] hover:border-[#0084ff]'
      } ${isSpeaking ? 'border-[#00ff00] animate-pulse' : ''}`}
      onClick={handleClick}
      style={{ cursor: isPinned ? 'default' : 'pointer' }}
    >
      {hasVideo ? (
        <video
          ref={setVideoRef}
          autoPlay
          playsInline
          muted={isLocalParticipant}
          className="w-full h-full object-cover"
          style={{ transform: isLocalParticipant ? 'scaleX(-1)' : 'none' }}
        />
      ) : (
        <div className={`w-full h-full flex items-center justify-center bg-neutral-800 text-white ${isPinned ? 'text-5xl' : 'text-2xl'}`}>
          <span className="font-bold">
            {identity.charAt(0).toUpperCase()}
          </span>
        </div>
      )}

      {!isLocalParticipant && hasAudio && (
        <audio ref={setAudioRef} autoPlay />
      )}

      {showControls && (
        <div className={`absolute bottom-2 left-2 right-2 flex items-center justify-between bg-black/70 rounded px-2 py-1 text-white ${isPinned ? 'text-sm' : 'text-[10px]'}`}>
          <span className="font-bold max-w-[60%] overflow-hidden text-ellipsis whitespace-nowrap">
            {isLocalParticipant ? 'You' : identity}
          </span>
          <div className="flex gap-1 items-center">
            <span className={`${hasAudio ? 'text-[#00ff00]' : 'text-[#ff0000]'}`}>
              {hasAudio ? '🎤' : '🔇'}
            </span>
            <span className={`${hasVideo ? 'text-[#00ff00]' : 'text-[#ff0000]'}`}>
              {hasVideo ? '📹' : '📵'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
